import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, mfaAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

// Restore the saved session synchronously so the first render already knows whether the
// user is signed in. This avoids a flash of the public header before the sidebar shell appears.
const readStoredUser = () => {
  const token = localStorage.getItem('token');
  const savedUser = localStorage.getItem('user');
  if (!token || !savedUser) return null;
  try {
    return JSON.parse(savedUser);
  } catch {
    localStorage.removeItem('user');
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(readStoredUser);
  // Kept for API compatibility with route guards; the session is available on first render.
  const loading = false;

  useEffect(() => {
    // Refresh the cached profile in the background so role/verification changes made
    // elsewhere (e.g. by an admin) are reflected without forcing a re-login.
    if (!user) return;
    authAPI.getProfile()
      .then((response) => {
        const fresh = response.data?.user;
        if (fresh) {
          localStorage.setItem('user', JSON.stringify(fresh));
          setUser(fresh);
        }
      })
      .catch((error) => {
        // The API interceptor already cleared storage on 401; mirror that in memory.
        if (error.response?.status === 401) setUser(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const _storeSession = (token, userData) => {
    sessionStorage.removeItem('manual_logout');
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const _storeUser = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      const { access_token, user: userData, mfa_required, mfa_token } = response.data;
      if (mfa_required) {
        return { success: true, mfa_required: true, mfa_token };
      }
      _storeSession(access_token, userData);
      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Login failed' };
    }
  };

  const verifyMFALogin = async (mfa_token, code) => {
    try {
      const response = await mfaAPI.verifyLogin(mfa_token, code);
      const { access_token, user: userData } = response.data;
      _storeSession(access_token, userData);
      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Invalid code' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      return {
        success: true,
        email: response.data.email,
        message: response.data.message,
      };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Registration failed' };
    }
  };

  const sendVerificationCode = async (email) => {
    try {
      await authAPI.sendVerificationCode(email);
      return { success: true };
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      const msg =
        data?.error ||
        data?.message ||
        (status === 429 ? 'Too many attempts. Please wait a minute and try again.' : null) ||
        (status === 503 ? 'Email service is unavailable. Please try again later.' : null) ||
        (status === 500 ? 'Server error. Please try again later.' : null) ||
        (!error.response ? 'Network error. Check your connection.' : null) ||
        'Failed to send code. Please try again.';
      return { success: false, error: msg };
    }
  };

  const verifyEmail = async (email, code) => {
    try {
      await authAPI.verifyEmail(email, code);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Verification failed' };
    }
  };

  const googleLogin = async (idToken) => {
    try {
      const response = await authAPI.googleVerify(idToken);
      const { access_token, user: userData } = response.data;
      _storeSession(access_token, userData);
      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Google sign-in failed' };
    }
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getProfile();
      const userData = response.data.user;
      _storeUser(userData);
    } catch {
      // silently fail — token may have expired
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await authAPI.changePassword({ current_password: currentPassword, new_password: newPassword });
      _storeUser(response.data.user);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to change password' };
    }
  };

  const updateProfilePicture = async (profilePictureUrl) => {
    try {
      const response = await authAPI.updateProfile({ profile_picture_url: profilePictureUrl });
      const userData = response.data.user;
      _storeUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to update profile picture' };
    }
  };

  const logout = () => {
    sessionStorage.setItem('manual_logout', '1');
    window.google?.accounts?.id?.disableAutoSelect();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    verifyMFALogin,
    register,
    logout,
    sendVerificationCode,
    verifyEmail,
    googleLogin,
    refreshUser,
    updateProfilePicture,
    changePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
